import React from 'react';
import { Card, CardBody, Input, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  CompanyDocumentType,
  PartnerDocumentType,
  IndividualDocumentType,
  DocumentOwnerType,
  CompanyDocumentDto,
  PartnerDocumentDto,
  IndividualDocumentDto,
} from '../../../../types/documents';

interface RequiredDocument {
  type: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType;
  label: string;
  isRequired: boolean;
  isOptional?: boolean;
}

interface RequiredDocumentsChecklistProps {
  ownerType: DocumentOwnerType;
  documents: (CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto)[];
  onDocumentClick?: (documentType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType) => void;
}

const RequiredDocumentsChecklist: React.FC<RequiredDocumentsChecklistProps> = ({
  ownerType,
  documents,
  onDocumentClick,
}) => {
  const { t } = useTranslation();

  // Helper function to normalize document type to integer
  // Handles both integer (correct) and string (legacy API) values
  const normalizeDocumentType = (docType: any): number => {
    if (typeof docType === 'number') {
      return docType;
    }
    // If it's a string, try to find the matching enum value
    if (typeof docType === 'string') {
      if (ownerType === DocumentOwnerType.Company) {
        const enumValue = CompanyDocumentType[docType as keyof typeof CompanyDocumentType];
        return typeof enumValue === 'number' ? enumValue : 99; // Default to Other
      } else if (ownerType === DocumentOwnerType.Partner) {
        const enumValue = PartnerDocumentType[docType as keyof typeof PartnerDocumentType];
        return typeof enumValue === 'number' ? enumValue : 99; // Default to Other
      } else if (ownerType === DocumentOwnerType.Individual) {
        const enumValue = IndividualDocumentType[docType as keyof typeof IndividualDocumentType];
        return typeof enumValue === 'number' ? enumValue : 99; // Default to Other
      }
    }
    return 99; // Default to Other if we can't parse it
  };

  // Define required documents for company
  const companyRequiredDocs: RequiredDocument[] = [
    {
      type: CompanyDocumentType.ContratoSocial,
      label: t('Contrato Social'),
      isRequired: true,
    },
    {
      type: CompanyDocumentType.CartaoCNPJ,
      label: t('Cartão CNPJ'),
      isRequired: true,
    },
    {
      type: CompanyDocumentType.ECNPJ,
      label: t('e-CNPJ'),
      isRequired: true,
    },
    {
      type: CompanyDocumentType.Alvara,
      label: t('Alvará de Funcionamento'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: CompanyDocumentType.InscricaoEstadual,
      label: t('Inscrição Estadual'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: CompanyDocumentType.InscricaoMunicipal,
      label: t('Inscrição Municipal'),
      isRequired: false,
      isOptional: true,
    },
  ];

  // Define required documents for partner
  const partnerRequiredDocs: RequiredDocument[] = [
    {
      type: PartnerDocumentType.CPF,
      label: t('CPF'),
      isRequired: true,
    },
    {
      type: PartnerDocumentType.RG,
      label: t('RG'),
      isRequired: true,
    },
    {
      type: PartnerDocumentType.CNH,
      label: t('CNH'),
      isRequired: true,
    },
    {
      type: PartnerDocumentType.ComprovanteResidencia,
      label: t('Comprovante de Residência'),
      isRequired: true,
    },
    {
      type: PartnerDocumentType.ECPF,
      label: t('e-CPF'),
      isRequired: false,
      isOptional: true,
    },
  ];

  // Define required documents for individual
  const individualRequiredDocs: RequiredDocument[] = [
    {
      type: IndividualDocumentType.RG,
      label: t('RG'),
      isRequired: true,
    },
    {
      type: IndividualDocumentType.CNH,
      label: t('CNH'),
      isRequired: true,
    },
    {
      type: IndividualDocumentType.ProofOfAddress,
      label: t('Comprovante de Endereço'),
      isRequired: true,
    },
    {
      type: IndividualDocumentType.ECPF,
      label: t('e-CPF'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: IndividualDocumentType.BirthCertificate,
      label: t('Certidão de Nascimento'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: IndividualDocumentType.MarriageCertificate,
      label: t('Certidão de Casamento'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: IndividualDocumentType.DivorceCertificate,
      label: t('Certidão de Divórcio'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: IndividualDocumentType.CPFCard,
      label: t('Cartão CPF'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: IndividualDocumentType.VoterRegistration,
      label: t('Título de Eleitor'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: IndividualDocumentType.WorkCard,
      label: t('Carteira de Trabalho'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: IndividualDocumentType.Proposta,
      label: t('Proposta'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: IndividualDocumentType.ContratoServico,
      label: t('Contrato de Prestação de Serviços'),
      isRequired: false,
      isOptional: true,
    },
    {
      type: IndividualDocumentType.AtaReuniao,
      label: t('Ata de Reunião'),
      isRequired: false,
      isOptional: true,
    },
  ];

  const requiredDocs =
    ownerType === DocumentOwnerType.Company
      ? companyRequiredDocs
      : ownerType === DocumentOwnerType.Partner
      ? partnerRequiredDocs
      : individualRequiredDocs;

  // Get all document types that are listed in required docs
  const listedTypes = new Set(requiredDocs.map(d => d.type));

  // Check if document exists
  const hasDocument = (docType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType): boolean => {
    return documents.some(
      (doc) =>
        normalizeDocumentType(doc.documentType) === docType &&
        !doc.isDeleted
    );
  };

  // Count documents for a specific type
  const countDocuments = (docType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType): number => {
    return documents.filter(
      (doc) =>
        normalizeDocumentType(doc.documentType) === docType &&
        !doc.isDeleted
    ).length;
  };

  // Count "other" documents (not in the listed types)
  const countOtherDocuments = (): number => {
    // DEBUG: Log document types to console
    console.log('=== DEBUG: Document Types Analysis ===');
    console.log('Listed types (expected):', Array.from(listedTypes));
    console.log('Documents received:', documents.map(d => ({
      fileName: d.originalFileName,
      documentType: d.documentType,
      normalized: normalizeDocumentType(d.documentType),
      typeOfValue: typeof d.documentType,
      isDeleted: d.isDeleted
    })));
    console.log('======================================');

    return documents.filter(
      (doc) =>
        !listedTypes.has(normalizeDocumentType(doc.documentType)) &&
        !doc.isDeleted
    ).length;
  };

  // Calculate completion percentage
  const requiredCount = requiredDocs.filter((d) => d.isRequired).length;
  const completedCount = requiredDocs.filter((d) => {
    if (d.isRequired) {
      return hasDocument(d.type);
    }
    return false;
  }).length;

  const otherDocCount = countOtherDocuments();

  return (
    <Card className="mb-3 border shadow-none">
      <CardBody className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 fs-14 text-muted">
            <i className="ri-file-list-3-line me-1"></i>
            {t('Lista de Documentos')}
          </h6>
          <span className="text-muted fs-12">
            {completedCount}/{requiredCount}
          </span>
        </div>

        <div className="table-responsive">
          <Table className="table-sm table-nowrap align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: '40px' }} className="text-center">
                  <i className="ri-checkbox-line"></i>
                </th>
                <th>{t('Nome')}</th>
                <th style={{ width: '100px' }} className="text-center">
                  {t('Obrigatório')}
                </th>
                <th style={{ width: '60px' }} className="text-center">
                  {t('Qtd')}
                </th>
              </tr>
            </thead>
            <tbody>
              {requiredDocs.map((doc, index) => {
                const isChecked = hasDocument(doc.type);
                const count = countDocuments(doc.type);

                return (
                  <tr key={index}>
                    <td className="text-center">
                      <Input
                        type="checkbox"
                        checked={isChecked}
                        disabled
                        className="form-check-input"
                        style={{ cursor: 'not-allowed' }}
                      />
                    </td>
                    <td>
                      {onDocumentClick ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-start text-decoration-none"
                          onClick={() => onDocumentClick(doc.type)}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {doc.label}
                        </button>
                      ) : (
                        <span>{doc.label}</span>
                      )}
                    </td>
                    <td className="text-center">
                      {doc.isRequired ? (
                        <i className="ri-checkbox-circle-fill text-success fs-16"></i>
                      ) : (
                        <i className="ri-close-circle-fill text-muted fs-16"></i>
                      )}
                    </td>
                    <td className="text-center">
                      <span className={`badge ${count > 0 ? 'bg-primary' : 'bg-secondary'}`}>
                        {count}
                      </span>
                    </td>
                  </tr>
                );
              })}

              {/* Others Row */}
              <tr>
                <td className="text-center">
                  <Input
                    type="checkbox"
                    checked={false}
                    disabled
                    className="form-check-input"
                    style={{ cursor: 'not-allowed' }}
                  />
                </td>
                <td>
                  {onDocumentClick ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-start text-decoration-none"
                      onClick={() => onDocumentClick(
                        ownerType === DocumentOwnerType.Company
                          ? CompanyDocumentType.Other
                          : ownerType === DocumentOwnerType.Partner
                          ? PartnerDocumentType.Other
                          : IndividualDocumentType.Other
                      )}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {t('Outros')}
                    </button>
                  ) : (
                    <span>{t('Outros')}</span>
                  )}
                </td>
                <td className="text-center">
                  <i className="ri-close-circle-fill text-muted fs-16"></i>
                </td>
                <td className="text-center">
                  <span className={`badge ${otherDocCount > 0 ? 'bg-info' : 'bg-secondary'}`}>
                    {otherDocCount}
                  </span>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default RequiredDocumentsChecklist;
